import {DiscreteTempo} from "../../global/types/DiscreteTempo";
import {createSectionFromJson} from "../../playable/utils/PlayableFactory";
import {schoolService} from "../SchoolService";
import {playablePool} from "../../playable/utils/PlayablePool";
import {defaultSection} from "../../playable/models/Section";
import {Version} from "../../playable/models/Version";
import { type Video } from "./Video";
import { type Exercise } from "./Exercise";

export type SchoolModuleStepType = 'EXERCISE' | 'VIDEO' | 'CHALLENGE' | 'VERSION'
export type ExerciseType = 'HANDPRINT' | 'SEQUENCE' | 'RHYTHMIC' | 'PIANISTIC_TECHNIC'

export abstract class SchoolModuleStep {
  moduleId: number;
  index: number;
  type: SchoolModuleStepType;

  abstract icon: string;

  get isFullyPremium(): boolean {
    return false;
  }

  protected constructor(json: any, moduleId: number, index: number) {
    this.moduleId = moduleId;
    this.index = index;
    this.type = json.step_type;
  }
}
export abstract class DisplayableSchoolModuleStep extends SchoolModuleStep {
  name: string;
  description: string;

  abstract icon: string;
  abstract get playerUrl(): string;

  protected constructor(json: any, moduleId: number, index: number) {
    super(json, moduleId, index)

    this.name = json.name;
    this.description = json.description;
  }
}

export class SchoolModuleStepExercise extends DisplayableSchoolModuleStep {
  exerciseType: ExerciseType;
  exerciseId: number;
  sectionId: number;
  startTempo: DiscreteTempo;
  musicxml: string;
  exercise: Exercise

  constructor(json: any, moduleId: number, index: number) {
    json.step_type = 'EXERCISE'; // Ensure the type is set correctly
    super(json, moduleId, index);
    this.exerciseType = json.type
    this.exerciseId = index !== null ? json.exercise_id : json.id
    this.startTempo = json.start_tempo
    this.sectionId = parseInt(index !== null ? json.exercise.section.id : json.section.id)
    this.musicxml = json.musicxml
    this.exercise = json

    createSectionFromJson(index !== null ?  json.exercise.section : json.section);
  }

  get icon(): string {
    return 'mdi-teach';
    /* for future implementation
    switch (this.exerciseType) {
      case "HANDPRINT":
        return 'mdi-close';
      case "SEQUENCE":
        return 'mdi-close';
      case "RHYTHMIC":
        return 'mdi-close';
      case "PIANISTIC_TECHNIC":
        return 'mdi-close';

      default:
        console.error(`Icon for exercise type ${this.exerciseType} is not handled...`)
        return 'mdi-close';
    }
    */
  }

  get playerUrl(): string {
    return `/player/school/exercise/${this.exerciseId}`;
  }

  get readableExerciseType() {
    switch (this.exerciseType) {
      case "HANDPRINT":
        return "Empreinte"

      case "PIANISTIC_TECHNIC":
        return "Technique pianistique"

      case "RHYTHMIC":
        return "Rythmique"

      case "SEQUENCE":
        return "Enchainement"

      default:
        console.error(`readable type not set for module step : ${this.type}`)
        return this.type
    }
  }

  get section() {
    if (schoolService.state.activeModule!) {
      return playablePool.get('section', this.sectionId);
    }
    return defaultSection;
  }
}
export class SchoolModuleStepVideo extends DisplayableSchoolModuleStep {
  icon: string = 'mdi-video';
  videoId: number;
  video: Video;

  constructor(json: any, moduleId: number, index: number) {
    super(json, moduleId, index);
    this.videoId = json.video_id;
    this.video = json.video;
  }

  get playerUrl(): string {
    return this.video.url;
  }
}
export class SchoolModuleStepChallenge extends DisplayableSchoolModuleStep {
  icon: string = 'mdi-trophy';
  challengeId: number;
  challenge: any;

  get isFullyPremium() {
    return (this.challenge.piece.catalog_type !== 'FREE')
  }

  constructor(json: any, moduleId: number, index: number) {
    super(json, moduleId, index);
    this.challengeId = json.challenge_id
    this.challenge = json.challenge
  }

  get playerUrl(): string {
    return `/player/school/challenge/${this.challengeId}`;
  }
}
export class SchoolModuleStepVersion extends DisplayableSchoolModuleStep { // TODO: Might use the same model for version & challenge
  icon: string = 'mdi-music';
  versionId: number;
  version: Version;

  get isFullyPremium() {
    return (this.version.piece.catalog_type !== 'FREE')
  }

  constructor(json: any, moduleId: number, index: number) {
    super(json, moduleId, index);
    this.versionId = json.version_id
    this.version = json.version
  }

  get playerUrl(): string {
    return `/player/version/${this.versionId}/${this.version.piece.url_artist}/${this.version.piece.url_title}`;
  }
}

export class ConcurrentSchoolModuleStep extends SchoolModuleStep { // TODO: Might use the same model for version & challenge
  public concurrentSteps: DisplayableSchoolModuleStep[] = [];

  get isFullyPremium() {
    return this.concurrentSteps.every((step: DisplayableSchoolModuleStep) => step.isFullyPremium)
  }

  constructor(json: any, moduleId: number, index: number) {
    super(json, moduleId, index);

    if (this.type === 'VERSION') {
      const firstVersionJson = Object.assign(json);
      firstVersionJson.version_id = firstVersionJson.first_version_id
      firstVersionJson.version = firstVersionJson.first_version

      this.concurrentSteps.push(new SchoolModuleStepVersion(firstVersionJson, moduleId, index))

      const secondVersionJson = Object.assign(json);
      secondVersionJson.version_id = secondVersionJson.second_version_id
      secondVersionJson.version = secondVersionJson.second_version

      this.concurrentSteps.push(new SchoolModuleStepVersion(secondVersionJson, moduleId, index))
    }
    else if (this.type === 'CHALLENGE') {
      const firstChallengeJson = Object.assign(json);
      firstChallengeJson.challenge_id = firstChallengeJson.first_challenge_id
      firstChallengeJson.challenge = firstChallengeJson.first_challenge

      this.concurrentSteps.push(new SchoolModuleStepChallenge(firstChallengeJson, moduleId, index))

      const secondChallengeJson = Object.assign(json);
      secondChallengeJson.challenge_id = secondChallengeJson.second_challenge_id
      secondChallengeJson.challenge = secondChallengeJson.second_challenge

      this.concurrentSteps.push(new SchoolModuleStepChallenge(secondChallengeJson, moduleId, index))
    }
    else {
      console.error(`${this.type} not handled for concurrent school steps.`)
    }
  }

  get icon() {
    return this.concurrentSteps[0].icon
  }
}

export function createSchoolStepFromJson(json: any, moduleId: number, index: number) {
  switch (json.step_type as SchoolModuleStepType) {
    case "EXERCISE":
      return new SchoolModuleStepExercise(json, moduleId, index);
    case "VIDEO":
      return new SchoolModuleStepVideo(json, moduleId, index);
    case "CHALLENGE":
      if (json.first_challenge && json.second_challenge) {
        return new ConcurrentSchoolModuleStep(json, moduleId, index)
      }
      else {
        const challengeJson = Object.assign(json);
        challengeJson.challenge_id = challengeJson.first_challenge_id
        challengeJson.challenge = challengeJson.first_challenge

        return new SchoolModuleStepChallenge(challengeJson, moduleId, index);
      }
    case "VERSION":
      if (json.first_version && json.second_version) {
        return new ConcurrentSchoolModuleStep(json, moduleId, index)
      }
      else {
        const versionJson = Object.assign(json);
        versionJson.version_id = versionJson.first_version_id
        versionJson.version = versionJson.first_version

        return new SchoolModuleStepVersion(versionJson, moduleId, index);
      }
    default:
      console.error(`Step of type ${json.step_type} as ${index} is not handled...`)
  }
}
